import { FormattedDate, FormattedMessage } from 'react-intl';
import { HorizontalOverflow } from '../../../components/HorizontalOverflow';
import formatDate from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import {
  Alert,
  Anchor,
  BreakoutWrapper,
  Button,
  Heading,
  I18NPluralTrips,
  Link,
  Loading,
  Table,
  Td,
  Text,
  Th,
  Tr,
  Spinner
} from '../../../components';
import { groupBy } from 'lodash';
import { FormattedCurrency } from '../../../components/FormattedCurrency';
import {
  CheckmarkAlt as CheckIcon,
  Clock as ClockIcon,
  CloseAlt as CrossIcon,
  Warning as WarningIcon
} from '@urbaninfrastructure/react-icons';
import {
  InvoiceSortField,
  invoicesQuery,
  invoicesQuery_currentUser_paginatedInvoices,
  invoicesQuery_currentUser_paginatedInvoices_rows,
  invoicesQuery_currentUser_paginatedInvoices_rows_InvoiceTrip,
  InvoiceStatus,
  OrderDirection,
  payInvoice,
  payInvoice_payInvoiceCharge_ConfirmationSessionStripe,
  payInvoiceVariables,
  PaymentDocumentType
} from '../../../core-types';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TablePager, useTablePager } from '../../../components/TablePager';
import { CURRENT_SUBSCRIPTION_FRAGMENT } from '../../../lib/core/fragments/currentSubscription';
import { StripeConfirmCardPayment } from '../../StripeConfirmCardPayment';
import { usePaymentCompletion } from '../../AsyncProcessCompletion';

const INVOICES_PER_PAGE = 25;

export const INVOICES_QUERY = gql`
  query invoicesQuery(
    $limit: Int
    $offset: Int
    $orderBy: InvoiceSortField
    $orderDirection: OrderDirection
    $after: IsoDateTime
    $before: IsoDateTime
    $statuses: [InvoiceStatus!]
  ) {
    currentUser {
      id
      paymentMethod {
        __typename
      }
      paginatedInvoices(
        pagination: { limit: $limit, offset: $offset }
        order: { orderBy: $orderBy, orderDirection: $orderDirection }
        filters: { after: $after, before: $before, statuses: $statuses }
      ) {
        ... on PaginatedInvoices {
          paginationInfo {
            hasNextPage
            hasPreviousPage
            totalRowCount
          }
          rows {
            __typename
            token
            ... on InvoiceTrip {
              id
              systemId
              status
              currency
              createdAt
              paidAt
              periodStart
              periodEnd
              payable
              total
              totalLimit
              items {
                __typename
                ... on InvoiceItemTrip {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const PAY_INVOICE_MUTATION = gql`
  ${CURRENT_SUBSCRIPTION_FRAGMENT}

  mutation payInvoice($invoiceId: ID!) {
    payInvoiceCharge(invoiceId: $invoiceId) {
      __typename
      ... on ChargeCompletedSubscription {
        subscription {
          ...CurrentSubscription
        }
      }
      ... on ConfirmationSessionStripe {
        clientSecret
        paymentMethodRef
        publishableKey
        paymentDocument {
          ... on InvoiceTrip {
            token
          }
          ... on InvoiceFine {
            token
          }
          ... on Order {
            token
          }
        }
      }
      ... on ChargeCompleted {
        paymentDocument {
          ... on InvoiceTrip {
            token
          }
        }
      }
      ... on ChargePending {
        paymentDocument {
          ... on InvoiceTrip {
            token
          }
          ... on InvoiceFine {
            token
          }
          ... on Order {
            token
          }
        }
      }
      ... on PaymentIntentError {
        description
        errorCode
        message
      }
    }
  }
`;

const InvoiceIcon = ({ status, ...restProps }: { status: InvoiceStatus }) => {
  if ([InvoiceStatus.open, InvoiceStatus.collection_failed].includes(status)) {
    return <WarningIcon color="state.warning" {...restProps} />;
  }
  if (status === InvoiceStatus.draft) {
    return <ClockIcon color="neutral.4" {...restProps} />;
  }
  if (status === InvoiceStatus.paid) {
    return <CheckIcon color="state.success" {...restProps} />;
  }
  return <CrossIcon color="neutral.4" {...restProps} />;
};

const DEFAULT_INVOICES_QUERY_VARIABLES = (offset = 0) => ({
  statuses: [
    InvoiceStatus.open,
    InvoiceStatus.collection_failed,
    InvoiceStatus.paid,
    InvoiceStatus.draft
  ],
  limit: INVOICES_PER_PAGE,
  offset: offset,
  orderBy: InvoiceSortField.createdAt,
  orderDirection: OrderDirection.ASC
});

const CellFormattedDate = ({ value }: { value: string | null }) => {
  if (!value) {
    return null;
  }

  return (
    <Text small>
      <FormattedDate year="numeric" month="short" day="numeric" value={value} />
    </Text>
  );
};

export const Invoices = () => {
  const { data, loading, refetch } = useQuery<invoicesQuery>(INVOICES_QUERY, {
    ssr: false,
    variables: DEFAULT_INVOICES_QUERY_VARIABLES()
  });

  let paginatedInvoicesData: invoicesQuery_currentUser_paginatedInvoices | null = null;

  if (data?.currentUser?.paginatedInvoices) {
    paginatedInvoicesData = data.currentUser.paginatedInvoices;
  }

  const { page, offset, openNextPage, openPrevPage } = useTablePager({
    perPage: INVOICES_PER_PAGE
  });

  const changePageAnchorElementRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    refetch(DEFAULT_INVOICES_QUERY_VARIABLES(offset));
    changePageAnchorElementRef.current?.focus();
  }, [offset]);

  const hasPaymentMethod = Boolean(data?.currentUser?.paymentMethod);

  const hasNextPage =
    paginatedInvoicesData?.paginationInfo?.hasNextPage || false;
  const isNextPageDisabled = !hasNextPage || loading;
  const hasPrevPage =
    paginatedInvoicesData?.paginationInfo?.hasPreviousPage || false;
  const isPrevPageDisabled = !hasPrevPage || loading;

  const totalInvoices =
    paginatedInvoicesData?.paginationInfo?.totalRowCount || 0;

  const hasInvoices = totalInvoices > 0;
  const isNeedPagination = totalInvoices > INVOICES_PER_PAGE;

  const [invoicesNotSorted, setInvoicesNotSorted] = useState<
    invoicesQuery_currentUser_paginatedInvoices_rows[] | undefined
  >(paginatedInvoicesData?.rows);

  useEffect(() => {
    setInvoicesNotSorted(paginatedInvoicesData?.rows);
  }, [paginatedInvoicesData?.rows]);
  const invoices = useMemo(() => {
    if (!invoicesNotSorted?.length) return [];

    const invoicesByStatus = groupBy(invoicesNotSorted, 'status');
    return [
      ...(invoicesByStatus[InvoiceStatus.collection_failed] || []),
      ...(invoicesByStatus[InvoiceStatus.open] || []),
      ...(invoicesByStatus[InvoiceStatus.draft] || []),
      ...(invoicesByStatus[InvoiceStatus.paid] || [])
    ];
  }, [invoicesNotSorted]);

  const updateInvoiceById = useCallback(
    (
      id: string,
      newData: Partial<
        invoicesQuery_currentUser_paginatedInvoices_rows_InvoiceTrip
      >
    ) => {
      setInvoicesNotSorted(prev => {
        if (!prev) return prev;

        const invoiceIndex = prev.findIndex(
          (i: invoicesQuery_currentUser_paginatedInvoices_rows_InvoiceTrip) =>
            i.id === id
        );
        if (invoiceIndex === -1) return prev;

        const newPrev = [...prev];
        newPrev[invoiceIndex] = {
          ...newPrev[invoiceIndex],
          ...newData
        } as invoicesQuery_currentUser_paginatedInvoices_rows;
        return newPrev;
      });
    },
    []
  );

  const [
    confirmationSessionStripeError,
    setConfirmationSessionStripeError
  ] = useState<boolean>(false);
  const [
    confirmationSessionStripeData,
    setConfirmationSessionStripeData
  ] = useState<
    payInvoice_payInvoiceCharge_ConfirmationSessionStripe | undefined
  >();
  const [chargePendingToken, setChargePendingToken] = useState<
    string | undefined
  >();
  const invoiceIdToPayRef = useRef<string | undefined>();
  const [payInvoiceRaw, payInvoiceResult] = useMutation<
    payInvoice,
    payInvoiceVariables
  >(PAY_INVOICE_MUTATION, {
    onCompleted: data => {
      switch (data.payInvoiceCharge.__typename) {
        case 'ConfirmationSessionStripe': {
          setConfirmationSessionStripeData(data.payInvoiceCharge);
          break;
        }
        case 'ChargeCompleted': {
          if (invoiceIdToPayRef.current) {
            updateInvoiceById(invoiceIdToPayRef.current, {
              status: InvoiceStatus.paid,
              paidAt: new Date().toISOString(),
              payable: false
            });
          }
          break;
        }
        case 'ChargePending': {
          setChargePendingToken(data.payInvoiceCharge.paymentDocument.token);
          break;
        }
        case 'PaymentIntentError':
        case 'ChargeCompletedSubscription': {
          break;
        }
        case 'RequirePaymentMethodStripe': {
          //TODO: only used for native apps
          break;
        }
      }
    }
  });
  const payInvoice = useCallback(
    (invoiceId: string) => {
      payInvoiceRaw({
        variables: {
          invoiceId
        }
      });
      invoiceIdToPayRef.current = invoiceId;
    },
    [payInvoiceRaw]
  );

  const chargePendingPolling = usePaymentCompletion({
    token: chargePendingToken,
    type: PaymentDocumentType.invoice,
    paymentState: 'success',
    skip: !chargePendingToken,
    onAsyncProcessCompleted: state => {
      switch (state) {
        case 'success': {
          if (invoiceIdToPayRef.current) {
            updateInvoiceById(invoiceIdToPayRef.current, {
              status: InvoiceStatus.paid,
              paidAt: new Date().toISOString(),
              payable: false
            });
          }
          setChargePendingToken(undefined);
          break;
        }
        case 'error':
        case 'cancelled':
        case 'failed': {
          setChargePendingToken(undefined);
          break;
        }
      }
    }
  });

  const isPaymentInProgress = Boolean(
    payInvoiceResult.loading ||
      confirmationSessionStripeData ||
      chargePendingPolling.loading
  );

  return (
    <>
      <Heading textAlign="center">
        <FormattedMessage id="invoices" defaultMessage="Invoices" />
      </Heading>
      {loading && <Loading justifyContent="center" />}
      {!loading && (
        <>
          {confirmationSessionStripeData && (
            <StripeConfirmCardPayment
              showSpinner={false}
              clientSecret={confirmationSessionStripeData.clientSecret}
              paymentMethod={confirmationSessionStripeData.paymentMethodRef}
              token={confirmationSessionStripeData.paymentDocument.token}
              type={PaymentDocumentType.invoice}
              publishableKey={confirmationSessionStripeData.publishableKey}
              onError={() => {
                setConfirmationSessionStripeError(true);
                setConfirmationSessionStripeData(undefined);
              }}
              onAsyncProcessCompleted={state => {
                switch (state) {
                  case 'success': {
                    if (invoiceIdToPayRef.current) {
                      updateInvoiceById(invoiceIdToPayRef.current, {
                        status: InvoiceStatus.paid,
                        paidAt: new Date().toISOString(),
                        payable: false
                      });
                    }
                    setConfirmationSessionStripeData(undefined);
                    setConfirmationSessionStripeError(false);
                    break;
                  }
                  case 'error':
                  case 'cancelled':
                  case 'failed': {
                    setConfirmationSessionStripeData(undefined);
                    setConfirmationSessionStripeError(true);
                    break;
                  }
                }
              }}
            />
          )}
          {confirmationSessionStripeError && (
            <Alert variant="error">
              <FormattedMessage
                id="StripeCheckout.confirmationSessionStripe.error"
                defaultMessage="Could not confirm your payment card. Please try again or add a new payment card."
              />
            </Alert>
          )}
          {payInvoiceResult?.data?.payInvoiceCharge.__typename ===
            'PaymentIntentError' && (
            <Alert variant="error" mb="sm">
              {payInvoiceResult?.data?.payInvoiceCharge.message}
            </Alert>
          )}
          {!hasInvoices && (
            <Alert variant="info">
              <FormattedMessage
                id="modules.Profile.Invoices.noInvoices"
                defaultMessage="You have no invoices"
              />
            </Alert>
          )}
          <div ref={changePageAnchorElementRef} tabIndex={0} />
          {hasInvoices && (
            <BreakoutWrapper>
              <HorizontalOverflow>
                <Table data-testid="Invoices__Table" variant="simple">
                  <thead>
                    <Tr>
                      <Th style={{ width: '44px' }} />
                      <Th>
                        <FormattedMessage
                          id="description"
                          defaultMessage="Description"
                        />
                      </Th>
                      <Th textAlign="center">
                        <FormattedMessage id="period" defaultMessage="Period" />
                      </Th>
                      <Th>
                        <FormattedMessage id="total" defaultMessage="Total" />
                      </Th>
                      <Th />
                      <Th />
                    </Tr>
                  </thead>
                  <tbody>
                    {invoices.map(
                      (
                        invoice: invoicesQuery_currentUser_paginatedInvoices_rows_InvoiceTrip
                      ) => {
                        if (!invoice) {
                          return null;
                        }

                        const tripsCount = invoice?.items?.length || 0;
                        const isCurrentInvoiceInPayment =
                          isPaymentInProgress &&
                          invoiceIdToPayRef.current === invoice.id;

                        return (
                          <Tr key={invoice.id}>
                            <Td
                              style={{
                                width: '44px',
                                paddingRight: 0,
                                verticalAlign: 'middle'
                              }}
                            >
                              <InvoiceIcon
                                status={invoice.status}
                                data-testid={`Invoices__Table__Row__${invoice.id}__Icon`}
                              />
                            </Td>
                            <Td width={290}>
                              {invoice.__typename === 'InvoiceTrip' && (
                                <Text bold>
                                  <span>{tripsCount} </span>
                                  <I18NPluralTrips
                                    values={{
                                      count: tripsCount
                                    }}
                                  />
                                </Text>
                              )}
                              {invoice.status ===
                                InvoiceStatus.collection_failed && (
                                <Text small>
                                  <FormattedMessage
                                    id="invoice.status.collection_failed"
                                    defaultMessage="Payment failed"
                                  />
                                </Text>
                              )}
                              {invoice.status === InvoiceStatus.open && (
                                <Text small>
                                  <FormattedMessage
                                    id="awaiting_payment"
                                    defaultMessage="Awaiting payment"
                                  />
                                </Text>
                              )}
                              {invoice.status === InvoiceStatus.draft && (
                                <Text small>
                                  <FormattedMessage
                                    id="modules.Profile.Invoices.scheduledCharge"
                                    defaultMessage={
                                      'Will be charged on {0} or when exceeds {1}'
                                    }
                                    values={{
                                      0: formatDate(
                                        parseISO(invoice.periodEnd),
                                        'd MMM yyyy'
                                      ),
                                      1: `${invoice.totalLimit} ${invoice.currency}`
                                    }}
                                  />
                                </Text>
                              )}
                              {invoice.paidAt && (
                                <Text small>
                                  <FormattedMessage
                                    id="paid_on"
                                    defaultMessage="Paid on {0}"
                                    values={{
                                      0: formatDate(
                                        parseISO(invoice.paidAt),
                                        'd MMM yyyy'
                                      )
                                    }}
                                  />
                                </Text>
                              )}
                            </Td>
                            <Td textAlign="center">
                              <CellFormattedDate value={invoice.periodStart} />
                              -
                              <CellFormattedDate value={invoice.periodEnd} />
                            </Td>
                            <Td verticalAlign="middle">
                              <FormattedCurrency value={invoice.total} />
                            </Td>
                            <Td textAlign="center" verticalAlign="middle">
                              {invoice.payable && (
                                <Button
                                  variant="primary"
                                  onClick={() => payInvoice(invoice.id)}
                                  size="small"
                                  disabled={
                                    isPaymentInProgress || !hasPaymentMethod
                                  }
                                >
                                  {isCurrentInvoiceInPayment && <Spinner />}
                                  {!isCurrentInvoiceInPayment && (
                                    <Text fontSize={0}>
                                      {invoice.status ===
                                        InvoiceStatus.draft && (
                                        <FormattedMessage
                                          id="pay_now"
                                          defaultMessage="Pay now"
                                        />
                                      )}
                                      {invoice.status ===
                                        InvoiceStatus.collection_failed && (
                                        <FormattedMessage
                                          id="trip.retry"
                                          defaultMessage="Retry"
                                        />
                                      )}
                                      {invoice.status ===
                                        InvoiceStatus.open && (
                                        <FormattedMessage
                                          id="pay"
                                          defaultMessage="Pay"
                                        />
                                      )}
                                    </Text>
                                  )}
                                </Button>
                              )}
                            </Td>
                            <Td verticalAlign="middle">
                              <Text small>
                                <Link
                                  route="invoices.plain"
                                  params={{ token: invoice.token }}
                                  passHref
                                >
                                  <Anchor
                                    data-testid={`Invoices__Table__Row__${invoice.id}__Link`}
                                  >
                                    <FormattedMessage
                                      id="modules.Profile.Invoices.table.viewInvoice"
                                      defaultMessage="View invoice"
                                    />
                                  </Anchor>
                                </Link>
                              </Text>
                            </Td>
                          </Tr>
                        );
                      }
                    )}
                  </tbody>
                </Table>
              </HorizontalOverflow>
              {isNeedPagination && (
                <TablePager
                  page={page}
                  onNextClick={openNextPage}
                  isNextDisabled={isNextPageDisabled}
                  onPrevClick={openPrevPage}
                  isPrevDisabled={isPrevPageDisabled}
                  buttonGroupProps={{
                    style: { marginTop: '1em' }
                  }}
                />
              )}
            </BreakoutWrapper>
          )}
        </>
      )}
    </>
  );
};
